<template>
  <div class="newsfeed-post card">

    <post-form v-if="isEditing" :entity-id="entityId" :entity="entity" :access-level="accessLevel"
      :old-content="postData.body" :old-title="postData.title" :is-new="false" :id="postData.id" :contentType="postData.contentType"
      @update-data="onUpdateData"
      @close-form="onCloseForm"/>

    <div class="newsfeed-post__data" v-if="!isEditing">
      <div class="newsfeed-post__header">
        <router-link class="newsfeed-post__avatar" :to="{
          name: 'UserProfile',
          params: { link: postData.author.link },
        }">
          <el-avatar :size="48">
            <user-avatar :userId="postData.author.userId"
              :alt="postData.author.firstName + ' ' + postData.author.lastName" />
          </el-avatar>
        </router-link>

        <div class="newsfeed-post__header-info">
          <router-link :to="{
            name: 'UserProfile',
            params: { link: postData.author.link },
          }">
            <div class="newsfeed-post__name">
              {{ postData.author.firstName }} {{ postData.author.lastName }}
            </div>
          </router-link>
          <div class="newsfeed-post__date">
            {{
              formatDate(
                postData.createdOn,
                SHORT_DATETIME_FORMAT,
              )
            }}
          </div>
        </div>
        
        <el-popover placement="bottom-end" width="64" trigger="hover" v-if="isUserLoggedIn && editable && postData.canEdit">
          <div class="newsfeed-post__actions-menu">
            <div class="newsfeed-post__action" @click="editPost" v-if="postData.canEdit">
              Edit
            </div>
            <div class="newsfeed-post__action" v-if="postData.canEdit" @click="requestDeletePost">
              Delete
            </div>
          </div>
          <template #reference>
            <el-icon >
              <More />
            </el-icon>
          </template>
        </el-popover>
      </div>

      <div class="newsfeed-post__content">
        <div v-if="postData.title != null && postData.title != ''" class="newsfeed-post__title" v-html="postData.title"></div>
        <div class="newsfeed-post__text" v-html="postData.body"></div>
      </div>

      <div class="newsfeed-post__bottom">
        <div class="newsfeed-post__comments" @click="toggleComments()">
          <icon :width="24" :height="24" glyph="comments" />
          <span class="newsfeed-post__comments-count">
            {{ postData.commentsCount > 0 ? postData.commentsCount : "" }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="showComments">
      <div class="newsfeed-post__comment" v-for="(comment, index) in paginatedComments" :key="index">
        <el-divider />
        <post-comment :comment-data="comment" @update-data="onUpdateData" :post-id="postData.id"
          :comment-id="comment.id" />
      </div>
      <div class="newsfeed-post__pagination">
        <el-button @click="fetchPreviousComments" :disabled="skip === 0">
          <el-icon><ArrowLeft /></el-icon>
        </el-button>
        <el-button @click="fetchNextComments" :disabled="postData.comments?.length < limit">
          <el-icon><ArrowRight /></el-icon>
        </el-button>
      </div>
    </div>

    <el-divider />

    <comment-form @update-data="onUpdateData" :post-id="postData.id" :is-new="true" v-if="isUserLoggedIn" />

    <div v-if="!isUserLoggedIn">
      <el-link type="primary">
        <router-link :to="{ name: 'Login' }">
          Sign in to add comments
        </router-link>
      </el-link>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';
import CommentForm from '@/components/common/CommentForm.vue';
import PostComment from '@/components/common/PostComment.vue';
import PostForm from '@/components/common/PostForm.vue';
import UserAvatar from '@/components/profile/UserAvatar.vue';

import { deleteBlogPost, getComments } from '@/api/blog.js';
import { SHORT_DATETIME_FORMAT, formatDate } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';

export default {
  name: 'NewsfeedPost',
  props: {
    postData: {
      type: Object,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    entity: {
      type: String,
      required: true,
    },
    isUserLoggedIn: {
      type: Boolean,
      required: false,
    },
    accessLevel: {
      type: String,
      required: true
    },
    id: {
      type: Number,
      required: false
    },
    editable: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    Icon,
    CommentForm,
    PostComment,
    PostForm,
    UserAvatar,
  },
  data() {
    return {
      isEditing: false,
      SHORT_DATETIME_FORMAT,
      showComments: false,
      limit: 5,
      skip: 0,
    };
  },
  computed: {
    paginatedComments() {
      return this.postData.comments || [];
    },
  },
  methods: {
    formatDate,
    showMessage,
    async requestDeletePost() {
      const { success } = await deleteBlogPost(this.postData.id);
      if (success) {
        this.postData.commentsCount--;
        this.$emit('update-data');
        this.showMessage({
          type: 'success',
          message: 'The post has been removed',
        });
      }
    },
    editPost() {
      this.isEditing = true;
    },
    onCloseForm() {
      this.isEditing = false;
    },
    onUpdateData() {
      this.$emit('update-data');
      this.showComments = true;
    },
    async toggleComments() {
      this.showComments = !this.showComments;
      if (this.showComments && this.postData.commentsCount > 0) {
        await this.fetchComments();
      }
    },
    async fetchComments() {
      const { postData, skip, limit } = this;
      const { data, success } = await getComments(postData.id, skip, limit);
      if (success) {
        postData.comments = data.items;
      }
    },
    async fetchPreviousComments() {
      if (this.skip >= this.limit) {
        this.skip -= this.limit;
        await this.fetchComments();
      }
    },
    async fetchNextComments() {
      this.skip += this.limit;
      await this.fetchComments();
    },
  },
};
</script>


<style lang="scss">
.newsfeed-post {
  margin-top: var(--global-dimension-size-250);

  &__data {
    line-height: 1.6;
  }

  &__header {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--global-dimension-size-250);
  }

  &__avatar {
    line-height: 1;
    margin-right: var(--global-dimension-size-125);
  }

  &__header-info {
    flex-grow: 1;
  }

  &__name {
    font-weight: 500;
    font-size: var(--font-size-300);
    color: var(--color-gray-900);
  }

  &__date {
    font-weight: normal;
    font-size: var(--font-size-75);
    color: var(--color-gray-700);
  }

  &__actions {
    cursor: pointer;
    padding-top: var(--global-dimension-size-125);
    padding-left: var(--global-dimension-size-125);
  }

  &__actions-menu {}

  &__action {
    display: block;
    padding: var(--global-dimension-size-75);
    color: var(--color-gray-900);
    cursor: pointer;
    font-size: var(--font-size-200);

    &:hover {
      color: var(--color-primary-400);
    }
  }

  &__content {
    *+* {
      margin-top: var(--global-dimension-size-250);
    }
  }

  &__photo {
    max-width: 100%;
  }

  &__text {
    font-weight: normal;
    font-size: var(--font-size-200);
    white-space: pre-wrap;
    word-break: break-word;
    max-width: 100%;
  }

  &__title {
    font-weight: bold;
    font-size: var(--font-size-300);
    color: var(--color-gray-900);
  }

  &__bottom {
    margin-top: var(--global-dimension-size-350);
    display: flex;
    flex-flow: row nowrap;
    align-items: centers;
  }

  &__comments {
    margin-right: var(--global-dimension-size-300);

    .icon {
      vertical-align: middle;
    }
  }

  &__comments-count {
    margin-left: var(--global-dimension-size-125);
    font-size: var(--font-size-200);
  }

  &__load-more {
    text-align: center;
    margin-top: var(--global-dimension-size-250);
  }

  &__pagination {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    margin-top: var(--global-dimension-size-250);
    align-items: center;
  }
}
</style>